import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { trackDiscoveryCallClick } from '@/utils/analytics';
import { handleDiscoveryCallClick } from '@/components/layout/Navbar';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AnimatedCounter } from '../ui/AnimatedCounter';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { LazyImage } from '@/components/ui/LazyImage';

export function Hero() {
  const { t } = useTranslation();
  const { links } = useSiteSettings();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const slides = [
    {
      id: 'organisation',
      shortLabel: t('hero.slide3.short', 'Organisations'),
      eyebrow: t('hero.slide3.eyebrow', 'For Organisations & HR Teams'),
      headline: t('hero.slide3.headline', 'Stop treating symptoms. Resolve the root cause of team friction.'),
      subhead: t('hero.slide3.subhead', 'A data-driven behavioral framework that moves beyond personality types to measure resilience, social drives, and vertical leadership maturity.'),
      image: '/five_lens_organisation_team_web.webp',
      primaryBtn: t('hero.slide3.btn1', 'Take the Free Team Diagnostic'),
      primaryLink: '/diagnostic',
      secondaryBtn: t('hero.slide3.btn2', 'Book a Discovery Call'),
      secondaryLink: links.bookingUrl,
      imageStyle: 'object-cover object-center w-full h-full'
    },
    {
      id: 'practice',
      shortLabel: t('hero.slide2.short', 'Coaches & Practitioners'),
      eyebrow: t('hero.slide2.eyebrow', 'For Coaches and Practitioners'),
      headline: t('hero.slide2.headline', 'Elevate your coaching. Deliver profound client insights.'),
      subhead: t('hero.slide2.subhead', 'Become a certified FIVE LENS practitioner. Leverage our multidimensional assessment tool to drive transformative results for your clients.'),
      image: '/five_lens_coaching_web.webp',
      primaryBtn: t('hero.slide2.btn1', 'Explore Certification'),
      primaryLink: '/practitioners',
      secondaryBtn: t('hero.slide2.btn2', 'Book a Discovery Call'),
      secondaryLink: links.bookingUrl,
      imageStyle: 'object-cover object-center w-full h-full'
    },
    {
      id: 'self',
      shortLabel: t('hero.slide1.short', 'Individuals'),
      eyebrow: t('hero.slide1.eyebrow', 'For Individuals'),
      headline: t('hero.slide1.headline', 'Unlock Human Potential. Expand your personal mastery.'),
      subhead: t('hero.slide1.subhead', 'A scientifically developed assessment tool that combines the wisdom of the Enneagram with vertical leadership maturity. Expand your self-awareness and potential.'),
      image: '/five_lens_personal_mastery_web.webp',
      primaryBtn: t('hero.slide1.btn1', 'Start Your Journey'),
      primaryLink: '/lens/personal-mastery',
      secondaryBtn: t('hero.slide1.btn2', 'Book a Discovery Call'),
      secondaryLink: links.bookingUrl,
      imageStyle: 'object-cover object-center w-full h-full'
    }
  ];

  // Auto-rotation every 7s; pauses on hover or manual pause
  useEffect(() => {
    if (isPaused || isHovered) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [isPaused, isHovered, currentSlide, slides.length]);

  const slide = slides[currentSlide];

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleSelectSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const renderHeadline = (text: string) => {
    const parts = text.split('. ');
    if (parts.length > 1) {
      return (
        <>
          {parts[0]}. <br />
          <span className="italic">{parts.slice(1).join('. ')}</span>
        </>
      );
    }
    return text;
  };

  return (
    <section 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative mt-20 flex flex-col lg:flex-row overflow-visible border-b border-ground-deep bg-white min-h-[calc(100vh-5rem)]"
    >
      <div 
        className="flex-1 flex flex-col justify-between px-6 sm:px-10 lg:px-16 py-10 lg:py-14 bg-white border-r border-ground-deep z-10 relative overflow-visible"
      >
        <div 
          className="absolute inset-0 pointer-events-none z-0 mix-blend-multiply opacity-[0.04]"
          style={{
            backgroundImage: 'url(/fingerprint-watermark.webp)',
            backgroundSize: '70% auto',
            backgroundPosition: 'left center',
            backgroundRepeat: 'no-repeat'
          }}
        />

        {/* Audience / Slide Selector Tabs: Clear, obvious manual navigation */}
        <div className="relative z-10 mb-6 sm:mb-8">
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 p-1 bg-ground-alt border border-ground-deep max-w-fit">
            {slides.map((s, idx) => {
              const isActive = currentSlide === idx;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => handleSelectSlide(idx)}
                  className={`px-3 py-1.5 sm:px-3.5 sm:py-2 text-[11px] sm:text-xs font-bold tracking-wider uppercase transition-all duration-200 flex items-center gap-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-rule-3 ${
                    isActive
                      ? 'bg-ink text-white shadow-sm'
                      : 'text-ink/75 hover:text-ink hover:bg-white'
                  }`}
                  aria-label={`View ${s.eyebrow}`}
                  aria-pressed={isActive}
                >
                  <span className={`text-[10px] font-mono ${isActive ? 'text-white/90 font-semibold' : 'text-ink/70'}`}>
                    0{idx + 1}
                  </span>
                  <span>{s.shortLabel}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-2xl relative z-10 my-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={`eyebrow-${currentSlide}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="mb-4 sm:mb-6 flex items-center gap-3"
            >
              <span className="h-[2px] w-8 bg-rule-3"></span>
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-rule-3">
                {slide.eyebrow}
              </span>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.h1 
              key={`headline-${currentSlide}`}
              initial={{ opacity: 0, x: -25 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 25 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              className="text-4xl sm:text-5xl lg:text-[56px] leading-[1.08] mb-6 font-bold tracking-tight text-ink"
            >
              {renderHeadline(slide.headline)}
            </motion.h1>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.div
              key={`body-${currentSlide}`}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <p className="text-base sm:text-lg text-ink/75 mb-8 sm:mb-10 leading-relaxed max-w-xl">
                {slide.subhead}
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4 mb-6 sm:mb-8">
                <a 
                  href={slide.primaryLink} 
                  className="w-full sm:w-auto inline-flex items-center justify-center bg-ink text-white px-7 py-3.5 uppercase tracking-wider text-xs font-bold hover:bg-ink/90 transition-colors text-center rounded-none shadow-sm"
                >
                  {slide.primaryBtn}
                </a>
                <a 
                  href={slide.secondaryLink} 
                  onClick={() => {
                    handleDiscoveryCallClick(slide.secondaryLink);
                    trackDiscoveryCallClick('hero');
                  }}
                  className="w-full sm:w-auto inline-flex items-center justify-center border border-ink text-ink px-7 py-3.5 uppercase tracking-wider text-xs font-bold hover:bg-ground transition-colors text-center rounded-none"
                >
                  {slide.secondaryBtn}
                </a>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom Bar: Stats + Prominent Carousel Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pt-6 sm:pt-8 border-t border-ground-deep mt-auto relative z-10">
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="text-4xl lg:text-5xl font-normal text-rule-3"><AnimatedCounter end={700} suffix="+" /></div>
            <div className="text-xs font-bold tracking-widest uppercase text-ink/80 leading-relaxed">
              {t('hero.certifiedPractitioners', 'Certified Practitioners Globally')}
            </div>
          </div>

          {/* Obvious Manual Navigation Controls */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs font-mono font-medium text-ink/60">
              <span className="font-bold text-ink">0{currentSlide + 1}</span>
              <span className="opacity-40">/</span>
              <span>0{slides.length}</span>
            </div>

            <div className="flex items-center border border-ground-deep bg-ground-alt shadow-sm">
              <button 
                type="button"
                onClick={handlePrev}
                aria-label={t('common.previous', 'Previous slide')}
                title="Previous slide"
                className="px-3 py-2 flex items-center gap-1 text-ink hover:bg-ink hover:text-white transition-colors cursor-pointer border-r border-ground-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-rule-3"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="text-[11px] font-bold uppercase tracking-wider hidden sm:inline">Prev</span>
              </button>
              <button 
                type="button"
                onClick={() => setIsPaused(!isPaused)}
                aria-label={isPaused ? "Resume auto-rotation" : "Pause auto-rotation"}
                title={isPaused ? "Resume auto-rotation" : "Pause auto-rotation"}
                className={`px-2.5 py-2 flex items-center justify-center transition-colors cursor-pointer border-r border-ground-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-rule-3 ${
                  isPaused ? 'text-rule-3 bg-rule-3/15 font-semibold' : 'text-ink hover:bg-ink hover:text-white'
                }`}
              >
                {isPaused ? <Play className="w-3.5 h-3.5 ml-0.5" /> : <Pause className="w-3.5 h-3.5" />}
              </button>
              <button 
                type="button"
                onClick={handleNext}
                aria-label={t('common.next', 'Next slide')}
                title="Next slide"
                className="px-3 py-2 flex items-center gap-1 text-ink hover:bg-ink hover:text-white transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-rule-3"
              >
                <span className="text-[11px] font-bold uppercase tracking-wider hidden sm:inline">Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Progress bar at the bottom of the section indicating auto-rotation status */}
        {!isPaused && (
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-ground-deep overflow-hidden">
            <motion.div
              key={currentSlide}
              initial={{ width: '0%' }}
              animate={{ width: isHovered ? '0%' : '100%' }}
              transition={{ duration: 7, ease: 'linear' }}
              className="h-full bg-rule-3"
            />
          </div>
        )}
      </div>

      <div className="lg:w-[50%] shrink-0 flex flex-col justify-center bg-white z-10 relative overflow-hidden h-[450px] lg:h-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 w-full h-full flex items-center justify-center bg-ground-deep"
          >
            <img
              src={slide.image}
              alt={slide.image === '/five_lens_organisation_team_web.webp' ? "Diverse executive team collaborating around a conference table using the FIVE LENS Enneagram framework" : slide.eyebrow}
              className={`w-full h-full ${slide.imageStyle}`}
              width={800}
              height={900}
              fetchPriority="high"
              referrerPolicy="no-referrer"
              onError={(e) => {
                const target = e.currentTarget;
                if (!target.src.includes('five_lens_organisation_team_web.webp')) {
                  target.src = '/five_lens_organisation_team_web.webp';
                }
              }}
            />
          </motion.div>
        </AnimatePresence>
        
        {/* Carousel Indicators / Navigation overlay on image */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-20">
          <button 
            type="button"
            onClick={handlePrev}
            aria-label={t('common.previous', 'Previous slide')}
            className="w-9 h-9 flex items-center justify-center rounded-full border border-ground-deep bg-white/95 backdrop-blur text-ink hover:bg-ink hover:text-white transition-colors shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-rule-3 cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 bg-ink/70 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/20">
            {slides.map((s, idx) => (
              <button
                key={s.id}
                type="button"
                onClick={() => handleSelectSlide(idx)}
                aria-label={`Go to slide ${idx + 1}: ${s.eyebrow}`}
                className={`rounded-full transition-all duration-300 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-rule-3 cursor-pointer min-w-[24px] min-h-[24px] flex items-center justify-center ${currentSlide === idx ? 'bg-rule-3' : 'bg-transparent hover:bg-white/20'}`}
                style={{ padding: 0 }}
              >
                <span className={`block h-2 rounded-full ${currentSlide === idx ? 'w-6 bg-rule-3' : 'w-2 bg-white/60'}`} />
              </button>
            ))}
          </div>
          <button 
            type="button"
            onClick={handleNext}
            aria-label={t('common.next', 'Next slide')}
            className="w-9 h-9 flex items-center justify-center rounded-full border border-ground-deep bg-white/95 backdrop-blur text-ink hover:bg-ink hover:text-white transition-colors shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-rule-3 cursor-pointer"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
